import { useCallback, useEffect, useRef, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ChirpError } from 'rpc-client-utils';
import { useUploadProfilePhotoMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { usePortalsPlatformObjectId } from '../context/PortalsPlatformObjectIdContext';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];
const PHOTO_ERROR_ALERT_ID = 'photo-upload-error';
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
export function useProfilePhotoUpload(loginVerifyData, refetchLoginVerify, alertStore) {
  var _ref;
  const [pendingFile, setPendingFile] = useState();
  const [localAvatarUrl, setLocalAvatarUrl] = useState();
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState();
  const [imageFailed, setImageFailed] = useState(false);
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();
  const [uploadProfilePhoto, {
    loading: uploading
  }] = useUploadProfilePhotoMutation();
  const localAvatarUrlRef = useRef(localAvatarUrl);
  // eslint-disable-next-line react-hooks/refs
  localAvatarUrlRef.current = localAvatarUrl;
  const errorDismissTimerRef = useRef(undefined);
  const addErrorAlert = useCallback(titleText => {
    clearTimeout(errorDismissTimerRef.current);
    alertStore === null || alertStore === void 0 || alertStore.addAlert({
      id: PHOTO_ERROR_ALERT_ID,
      type: 'danger',
      titleText
    });
    errorDismissTimerRef.current = setTimeout(() => {
      alertStore === null || alertStore === void 0 || alertStore.removeAlert(PHOTO_ERROR_ALERT_ID);
    }, 5000);
  }, [alertStore]);
  const displayUrl = (_ref = localAvatarUrl !== null && localAvatarUrl !== void 0 ? localAvatarUrl : uploadedAvatarUrl) !== null && _ref !== void 0 ? _ref : loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.avatarUrl;
  useEffect(() => {
    return () => {
      if (localAvatarUrlRef.current) {
        URL.revokeObjectURL(localAvatarUrlRef.current);
      }
    };
  }, []);
  const showImage = Boolean(displayUrl) && !imageFailed;
  const handleImageError = useCallback(() => {
    setImageFailed(true);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
      setLocalAvatarUrl(undefined);
      addErrorAlert( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.profilePhoto.errorUploadFailed"
      }));
    }
  }, [localAvatarUrl, addErrorAlert]);
  const resolveUploadErrorMessage = useCallback(error => {
    var _networkError$cause, _networkError;
    const rpcError = (_networkError$cause = error === null || error === void 0 || (_networkError = error.networkError) === null || _networkError === void 0 ? void 0 : _networkError.cause) !== null && _networkError$cause !== void 0 ? _networkError$cause : error === null || error === void 0 ? void 0 : error.cause;
    const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
    switch (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) {
      case 'FILE_TOO_LARGE':
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorFileTooLarge');
      case 'UNSUPPORTED_FILE_TYPE':
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorInvalidType');
      default:
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorUploadFailed');
    }
  }, []);
  const handleFileChange = useCallback(event => {
    var _event$target$files;
    const file = (_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      addErrorAlert( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.profilePhoto.errorInvalidType"
      }));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      addErrorAlert( /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.profilePhoto.errorFileTooLarge"
      }));
      return;
    }
    clearTimeout(errorDismissTimerRef.current);
    alertStore === null || alertStore === void 0 || alertStore.removeAlert(PHOTO_ERROR_ALERT_ID);
    setImageFailed(false);
    const previewUrl = URL.createObjectURL(file);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
    }
    setLocalAvatarUrl(previewUrl);
    setPendingFile(file);
  }, [localAvatarUrl, addErrorAlert, alertStore]);
  const save = useCallback(async () => {
    if (!pendingFile) {
      return true;
    }
    const previewUrl = localAvatarUrl;
    try {
      var _data$result;
      const imageData = await fileToBase64(pendingFile);
      const {
        data
      } = await uploadProfilePhoto({
        variables: {
          imageData,
          contentType: pendingFile.type,
          accountId,
          portalInstanceId,
          objectId
        }
      });
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setLocalAvatarUrl(undefined);
      setPendingFile(undefined);
      const avatarUrl = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.avatarUrl;
      if (avatarUrl) {
        setUploadedAvatarUrl(avatarUrl);
      }
      await (refetchLoginVerify === null || refetchLoginVerify === void 0 ? void 0 : refetchLoginVerify());
      return true;
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setLocalAvatarUrl(undefined);
      setPendingFile(undefined);
      addErrorAlert(resolveUploadErrorMessage(error));
      return false;
    }
  }, [pendingFile, localAvatarUrl, uploadProfilePhoto, accountId, portalInstanceId, objectId, resolveUploadErrorMessage, refetchLoginVerify, addErrorAlert]);
  return {
    displayUrl,
    showImage,
    uploading,
    handleFileChange,
    handleImageError,
    save
  };
}